// The unfinished games you're seated in or running. Players can rejoin or give up their seat;
// Storytellers can rejoin or end the game.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hourglass } from "lucide-react";
import type { LiveGameSummary } from "@cc/shared";
import type { Guild } from "../../state/auth.js";
import { useGame } from "../../state/game.js";
import { Button, Dialog, DialogContent } from "../ui/primitives.js";
import { GuildIcon } from "../ui/GuildSelect.js";
import { EndGameForm } from "../storyteller/EndGameForm.js";

function phaseLabel(phase: LiveGameSummary["phase"]): string {
  if (phase.kind === "setup") return "Lobby";
  return `${phase.kind === "night" ? "Night" : "Day"} ${phase.number}`;
}

function lastActive(at: number, now: number): string {
  const minutes = Math.max(0, Math.floor((now - at) / 60_000));
  if (minutes < 1) return "active just now";
  if (minutes < 60) return `last active ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return `last active ${hours} hr${hours === 1 ? "" : "s"} ago`;
}

/** The End Game dialog for a game you're not in right now, with the option to delete it outright. */
export function EndGameDialog({
  code,
  onClose,
  onEnded,
}: {
  code: string | null;
  onClose: () => void;
  onEnded: () => void;
}) {
  const send = useGame((s) => s.send);
  const end = async (result: { winningTeam: "good" | "evil"; notes: string } | null): Promise<boolean> => {
    if (!code) return false;
    const ack = await send("game:end", { code, result });
    if (ack.ok) onEnded();
    return ack.ok;
  };
  return (
    <Dialog open={code !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={`End game ${code ?? ""}`}
      >
        <EndGameForm onEnd={(result) => end(result)} onDelete={() => end(null)} />
      </DialogContent>
    </Dialog>
  );
}

export function LiveGames({
  games,
  guilds,
  now,
  onChanged,
}: {
  games: LiveGameSummary[];
  guilds: Guild[];
  now: number;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const { joinGame, send } = useGame();
  const [leaving, setLeaving] = useState<string | null>(null);
  const [ending, setEnding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (games.length === 0) return null;

  const rejoin = async (code: string): Promise<void> => {
    setError(null);
    const ack = await joinGame(code);
    if (ack.ok) navigate(`/game/${code}`);
    else setError(ack.error);
  };

  const leave = async (code: string): Promise<void> => {
    const ack = await send("game:resign", { code });
    setLeaving(null);
    if (!ack.ok) setError(ack.error);
    onChanged();
  };

  return (
    <section className="space-y-3 rounded-xl border border-brass/25 bg-ink-soft/75 p-5 shadow-2xl backdrop-blur-md">
      <h2 className="flex items-center gap-2 font-serif text-lg font-semibold text-parchment">
        <Hourglass className="h-4 w-4 text-brass" aria-hidden="true" />
        Your live games
      </h2>
      <ul className="space-y-2">
        {games.map((game) => {
          const guild = guilds.find((g) => g.id === game.guildId);
          const isStoryteller = game.role === "storyteller";
          return (
            <li key={game.code} className="flex items-center gap-3 rounded-md border border-ink-line bg-ink/50 p-2.5">
              {guild && <GuildIcon guild={guild} className="size-8" />}
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm text-parchment">
                  <span className="font-mono tracking-widest">{game.code}</span>
                  <span className="text-xs text-parchment-dim">
                    {isStoryteller ? "Storyteller" : "Player"} · {phaseLabel(game.phase)} · {game.playerCount} seated
                  </span>
                </p>
                <p className="truncate text-xs text-parchment-dim">
                  {lastActive(game.lastActivity, now)}
                  {guild ? ` · ${guild.name}` : ""}
                </p>
              </div>
              <Button size="sm" variant="primary" onClick={() => void rejoin(game.code)}>
                Rejoin
              </Button>
              {isStoryteller ? (
                <Button size="sm" variant="danger" onClick={() => setEnding(game.code)}>
                  End
                </Button>
              ) : (
                <Button size="sm" onClick={() => setLeaving(game.code)}>
                  Leave
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className="text-xs text-evil">{error}</p>}

      <Dialog open={leaving !== null} onOpenChange={(open) => !open && setLeaving(null)}>
        <DialogContent title={`Leave game ${leaving ?? ""}?`} className="w-[min(24rem,calc(100vw-2rem))]">
          <p className="text-sm text-parchment-dim">
            You&apos;ll lose your seat, and the table will be told you&apos;ve left. Once the game is under way, you can only come back as a Traveler.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setLeaving(null)}>
              Stay
            </Button>
            <Button variant="danger" onClick={() => leaving && void leave(leaving)}>
              Leave the game
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EndGameDialog
        code={ending}
        onClose={() => setEnding(null)}
        onEnded={() => {
          setEnding(null);
          onChanged();
        }}
      />
    </section>
  );
}
